import { useEffect, useState } from 'react';
import { api, adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Settings, Save, Instagram, Facebook, MessageCircle } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    social_links: { instagram: '', facebook: '', tiktok: '', whatsapp: '' },
    about_text: '',
    hero_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings({
        social_links: data.social_links || { instagram: '', facebook: '', tiktok: '', whatsapp: '' },
        about_text: data.about_text || '',
        hero_image_url: data.hero_image_url || ''
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Paramètres enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSocialLink = (key, value) => {
    setSettings({
      ...settings,
      social_links: { ...settings.social_links, [key]: value }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-medium tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground mt-1">Personnalisez votre portfolio</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Réseaux sociaux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                <Instagram className="h-4 w-4" />
                Instagram
              </label>
              <Input
                value={settings.social_links.instagram}
                onChange={(e) => updateSocialLink('instagram', e.target.value)}
                placeholder="https://instagram.com/votre-profil"
                data-testid="settings-instagram"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                <Facebook className="h-4 w-4" />
                Facebook
              </label>
              <Input
                value={settings.social_links.facebook}
                onChange={(e) => updateSocialLink('facebook', e.target.value)}
                placeholder="https://facebook.com/votre-page"
                data-testid="settings-facebook"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                <TikTokIcon className="h-4 w-4" />
                TikTok
              </label>
              <Input
                value={settings.social_links.tiktok}
                onChange={(e) => updateSocialLink('tiktok', e.target.value)}
                placeholder="https://tiktok.com/@votre-profil"
                data-testid="settings-tiktok"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                WhatsApp (numéro)
              </label>
              <Input
                value={settings.social_links.whatsapp}
                onChange={(e) => updateSocialLink('whatsapp', e.target.value)}
                placeholder="+33612345678"
                data-testid="settings-whatsapp"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Entrez votre numéro avec l'indicatif pays (ex: +33 pour la France)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* About Text */}
        <Card>
          <CardHeader>
            <CardTitle>Texte "À propos"</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              placeholder="Présentez-vous en quelques lignes..."
              rows={6}
              data-testid="settings-about"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Ce texte s'affichera dans la section "À propos" de votre portfolio
            </p>
          </CardContent>
        </Card>

        {/* Hero Image */}
        <Card>
          <CardHeader>
            <CardTitle>Image du Hero</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={settings.hero_image_url}
              onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })}
              placeholder="https://.../votre-image.jpg"
              data-testid="settings-hero-image"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Laissez vide pour utiliser l'image par défaut de la page d'accueil
            </p>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="settings-save">
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer les paramètres
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
